import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../../../components/form-control/input-text";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from 'primereact/card';
import { Skeleton } from "primereact/skeleton";
import apiFunctions from "../../../../services/api-service";
import useToast from "../../../../components/common/useToast";
import { AxiosError } from "axios";
import { Select } from "antd";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { useDispatch } from "react-redux";
import { ArrowLeftIcon, CheckIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useJsApiLoader } from "@react-google-maps/api";

interface LocationPrice {
    _id?: string;
    placeId: string;
    locationName: string;
    country: string;
    state: string;
    city: string;
    price: string;
}

interface FormValues {
    name: string;
    type: string;
    days: string;
    badgelabel: string;
    section: string;
    base_price: string;
    location_prices: LocationPrice[];
}

const TYPE_OPTIONS = [
    { label: "Banner", value: "image" },
    { label: "Video", value: "video" }
];

const SECTION_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Home Post", value: "homepost" },
    { label: "Home Story", value: "homestory" },
    { label: "MaitriMonial", value: "maitrimonial" },
    { label: "Directory", value: "users" },
    { label: "Marketplace", value: "business" },
    { label: "Bazar Bhav", value: "bazarbhav" },
    { label: "Jobs", value: "job" },
    { label: "Blood Group", value: "bloodgroup" },
    { label: "Notification", value: "notification" },
];

const AdvertisementPackageCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();
    const dispatch = useDispatch();

    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(!!id);

    const locationInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
        libraries: ["places"]
    });

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name required"),
        days: Yup.string().required("Days required"),
        type: Yup.string().required("Type required"),
        section: Yup.string().required("Section required"),
        base_price: Yup.number().required("Base price required"),
    });

    const initialValue: FormValues = {
        name: "",
        type: "",
        days: "",
        badgelabel: "",
        section: "",
        base_price: "",
        location_prices: []
    };

    const formik = useFormik<FormValues>({
        initialValues: initialValue,
        validationSchema: validationSchema,

        onSubmit: async (values: any) => {
            let payload = {
                name: values.name,
                days: Number(values.days),
                badgelabel: values.badgelabel,
                type: values.type,
                section: values.section,
                base_price: Number(values.base_price),
                location_prices: values.location_prices
            };
            if (id) {
                await apiFunctions.put(`/advertisementpackage/${id}`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({ severity: "success", summary: "Success", detail: response.data.message, life: 3000 });
                        } else {
                            showToast({ severity: "error", summary: "Error", detail: response.data.message, life: 3000 });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) { dispatch(logout()); persistor.purge(); navigate("/admin/login"); }
                        errorHandler(err);
                    });
            } else {
                await apiFunctions.post(`/advertisementpackage`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({ severity: "success", summary: "Success", detail: response.data.message, life: 3000 });
                        } else {
                            showToast({ severity: "error", summary: "Error", detail: response.data.message, life: 3000 });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) { dispatch(logout()); persistor.purge(); navigate("/admin/login"); }
                        errorHandler(err);
                    });
            }
        },
    });

    const getEducationRuleData = async (packageId: string) => {
        try {
            await apiFunctions.get(`/advertisementpackage/${packageId}`)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        const data = response.data.data;
                        formik.setValues({
                            name: data.name,
                            days: data.days,
                            badgelabel: data.badgelabel,
                            type: data.type,
                            section: data.section,
                            base_price: data.base_price,
                            location_prices: (data.location_prices || []).map((loc: any) => ({
                                _id: loc._id,
                                placeId: loc.placeId || "",
                                locationName: loc.locationName || loc.village?.name || loc.district?.name || "",
                                country: typeof loc.country === "string" ? loc.country : (loc.country?.name || ""),
                                state: typeof loc.state === "string" ? loc.state : (loc.state?.name || ""),
                                city: loc.city || (typeof loc.district === "string" ? loc.district : (loc.district?.name || "")),
                                price: loc.price ? String(loc.price) : ""
                            }))
                        });
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response.data.message, life: 3000 });
                    }
                })
                .catch((err: AxiosError) => { errorHandler(err); })
                .finally(() => { setLoading(false); });
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (id) getEducationRuleData(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize Google Autocomplete for newly opened editing rows
    useEffect(() => {
        if (!isLoaded || editingIndex === null) return;
        const timer = setTimeout(() => {
            const inputEl = locationInputRefs.current[editingIndex];
            if (!inputEl) return;

            const autocomplete = new (window as any).google.maps.places.Autocomplete(inputEl);
            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (!place.place_id) return;

                const components = place.address_components || [];
                const getComp = (type: string) =>
                    components.find((c: any) => c.types.includes(type))?.long_name || "";

                const updated = [...formik.values.location_prices];
                updated[editingIndex] = {
                    ...updated[editingIndex],
                    placeId: place.place_id,
                    locationName: place.formatted_address || place.name || "",
                    city: getComp("locality") || getComp("administrative_area_level_3") || getComp("administrative_area_level_2"),
                    state: getComp("administrative_area_level_1"),
                    country: getComp("country"),
                };
                formik.setFieldValue("location_prices", updated);
            });
        }, 100);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, editingIndex]);

    const handleBack = () => navigate('/admin/advertisementpackage');

    const addLocationPrice = () => {
        const updated = [...formik.values.location_prices];
        updated.push({
            _id: crypto.randomUUID(),
            placeId: "",
            locationName: "",
            country: "",
            state: "",
            city: "",
            price: ""
        });
        formik.setFieldValue("location_prices", updated);
        setEditingIndex(updated.length - 1);
    };

    const removeLocationPrice = (index: number) => {
        const updated = [...formik.values.location_prices];
        updated.splice(index, 1);
        formik.setFieldValue("location_prices", updated);
        if (editingIndex === index) setEditingIndex(null);
    };

    const getLocationTitle = (loc: LocationPrice, index: number) => {
        const parts = [loc.city, loc.state, loc.country].filter(Boolean);
        const display = loc.locationName || parts.join(", ") || `Location ${index + 1}`;
        const price = loc.price ? `₹${loc.price}` : "";
        return `Location ${index + 1}: ${display}${price ? ` (${price})` : ""}`;
    };

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Advertisement Rule</h3>) : (<h3>Add Advertisement Rule</h3>)}
            </div>
            <div className="pb-4">
                <form action="submit" onSubmit={formik.handleSubmit}>
                    <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="120px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="90px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="50px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="140px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-2">
                                <div className="field mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Name"
                                    placeholder=""
                                    modelValue={formik.values.name}
                                    disabled={false}
                                    autofocus={true}
                                    id="name"
                                    validation={
                                        formik.errors.name && formik.touched.name && {
                                            $error: true,
                                            required: { $invalid: true, $message: formik.errors.name },
                                        }
                                    }
                                    onUpdateModelValue={(value: string) => formik.setFieldValue("name", value)}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-6">
                                <div className="field">
                                    <label htmlFor="type" className="form-label">Type</label>
                                    <Select
                                        style={{ width: "100%", height: "42px" }}
                                        placeholder="Select Type"
                                        value={formik.values.type}
                                        onChange={(value) => formik.setFieldValue("type", value)}
                                        options={TYPE_OPTIONS}
                                    />
                                    {formik.touched.type && typeof formik.errors.type === "string" && (
                                        <span className="p-error text-left mt-2">{formik.errors.type}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <div className="col-span-12 lg:col-span-6">
                                <div className="field">
                                    <label htmlFor="section" className="form-label">Section (Screen)</label>
                                    <Select
                                        style={{ width: "100%", height: "42px" }}
                                        placeholder="Select Section"
                                        value={formik.values.section}
                                        onChange={(value) => formik.setFieldValue("section", value)}
                                        options={SECTION_OPTIONS}
                                    />
                                    {formik.touched.section && typeof formik.errors.section === "string" && (
                                        <span className="p-error text-left mt-2">{formik.errors.section}</span>
                                    )}
                                </div>
                            </div>
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Base Price"
                                    placeholder=""
                                    modelValue={formik.values.base_price}
                                    disabled={false}
                                    autofocus={false}
                                    id="base_price"
                                    validation={
                                        formik.errors.base_price && formik.touched.base_price && {
                                            $error: true,
                                            required: { $invalid: true, $message: formik.errors.base_price },
                                        }
                                    }
                                    onUpdateModelValue={(value: string) => formik.setFieldValue("base_price", value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Days"
                                    placeholder=""
                                    modelValue={formik.values.days}
                                    disabled={false}
                                    autofocus={false}
                                    id="days"
                                    validation={
                                        formik.errors.days && formik.touched.days && {
                                            $error: true,
                                            required: { $invalid: true, $message: formik.errors.days },
                                        }
                                    }
                                    onUpdateModelValue={(value: string) => formik.setFieldValue("days", value)}
                                />
                            </div>
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Badge Label (Optional)"
                                    placeholder=""
                                    modelValue={formik.values.badgelabel}
                                    disabled={false}
                                    autofocus={false}
                                    id="badgelabel"
                                    onUpdateModelValue={(value: string) => formik.setFieldValue("badgelabel", value)}
                                />
                            </div>
                        </div>

                        <div className="mt-4">
                            <h4>Location Prices</h4>

                            {formik.values.location_prices.map((loc, index) => (
                                <div key={loc._id || index} className="border rounded-lg mt-4 bg-gray-50">
                                    {/* TITLE BAR */}
                                    <div className="flex justify-between items-center font-semibold text-blue-600 bg-blue-50 px-3 py-2 border">
                                        <div>{getLocationTitle(loc, index)}</div>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                className="p-button-text p-button-sm"
                                                onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                                            >
                                                {editingIndex === index
                                                    ? <CheckIcon className="w-5 h-5 text-green-600" />
                                                    : <PencilIcon className="w-5 h-5 text-blue-600" />
                                                }
                                            </Button>
                                            <Button
                                                type="button"
                                                className="p-button-text p-button-sm p-button-danger"
                                                onClick={() => removeLocationPrice(index)}
                                            >
                                                <TrashIcon className="w-5 h-5 text-red-600" />
                                            </Button>
                                        </div>
                                    </div>

                                    {editingIndex === index && (
                                        <div className="p-4 grid grid-cols-12 gap-4">
                                            {/* Google Maps Place Autocomplete */}
                                            <div className="col-span-12 lg:col-span-6">
                                                <div className="field">
                                                    <label className="form-label">Location</label>
                                                    <div className="relative">
                                                        <input
                                                            ref={(el) => { locationInputRefs.current[index] = el; }}
                                                            type="text"
                                                            placeholder="Search location..."
                                                            defaultValue={loc.locationName}
                                                            onChange={(e) => {
                                                                if (!e.target.value) {
                                                                    const updated = [...formik.values.location_prices];
                                                                    updated[index] = { ...updated[index], placeId: "", locationName: "", city: "", state: "", country: "" };
                                                                    formik.setFieldValue("location_prices", updated);
                                                                }
                                                            }}
                                                            className="w-full border border-gray-300 rounded px-3 pr-8 text-sm focus:outline-none focus:border-blue-500"
                                                            style={{ height: "42px" }}
                                                        />
                                                        {loc.locationName && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = [...formik.values.location_prices];
                                                                    updated[index] = { ...updated[index], placeId: "", locationName: "", city: "", state: "", country: "" };
                                                                    formik.setFieldValue("location_prices", updated);
                                                                    if (locationInputRefs.current[index]) {
                                                                        locationInputRefs.current[index]!.value = "";
                                                                    }
                                                                }}
                                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                    {loc.city && (
                                                        <div className="mt-1 text-xs text-gray-500 flex gap-3">
                                                            {loc.city && <span>City: <strong>{loc.city}</strong></span>}
                                                            {loc.state && <span>State: <strong>{loc.state}</strong></span>}
                                                            {loc.country && <span>Country: <strong>{loc.country}</strong></span>}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Price */}
                                            <div className="col-span-12 lg:col-span-6">
                                                <InputText
                                                    type="text"
                                                    label="Price"
                                                    placeholder=""
                                                    modelValue={loc.price}
                                                    disabled={false}
                                                    autofocus={false}
                                                    id={`price_${index}`}
                                                    onUpdateModelValue={(val) => {
                                                        const updated = [...formik.values.location_prices];
                                                        updated[index] = { ...updated[index], price: val };
                                                        formik.setFieldValue("location_prices", updated);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <Button
                                className={formik.values.location_prices.length > 0 ? "mt-2" : ""}
                                type="button"
                                label="Add Location Price"
                                onClick={addLocationPrice}
                            />
                        </div>

                        <div className="grid grid-cols-12 gap-4 mt-2">
                            <div className="field mb-4">
                                {id ? (
                                    <Button label="Edit" className="prime-button btn-login w-max" type="submit" />
                                ) : (
                                    <Button label="ADD" className="prime-button btn-login w-max" type="submit" />
                                )}
                            </div>
                        </div>
                        </>
                    )}
                    </Card>
                </form>
            </div>
        </>
    );
};

export default AdvertisementPackageCreate;
